/**
 * ToolSelector — unified top navigation bar (single slim row, native-app style).
 *
 * Layout (desktop):
 *   [BrandMark]  |  Dashboard · Learning · Calculators · Community · More ▾   |  [Search] [🔔] [User ▾]
 * Layout (mobile):
 *   [BrandMark]                                                                   [☰] [🔍] [🔔]
 *
 * The 4 always-visible items are the highest-frequency destinations. Everything
 * else lives in a popover "More" grid so the bar stays clean and the search box
 * never gets clipped by pill overflow.
 */
import React, { useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BookOpen, Bot, Calculator, ChefHat, Beaker, Video, Users,
  StoreIcon, User as UserIcon, LayoutDashboard, Award,
  Search, Command as CommandIcon, ChevronDown, Menu, LogOut,
  ShoppingCart, Sparkles, Settings, BarChart3, GraduationCap,
  Bug,
} from 'lucide-react';
import AppBreadcrumbs from './AppBreadcrumbs';
import BrandMark from './BrandMark';
import TopicsNavDropdown from './TopicsNavDropdown';
import NotificationBell from './NotificationBell';
import DMInboxButton from './DMInboxButton';
import { LifeBuoy } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import LanguageSwitcher from './utility/LanguageSwitcher';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useCommandPalette } from './CommandPalette';
import { AuthContext } from '../App';

// Primary items — always visible on desktop.
const PRIMARY = [
  { icon: LayoutDashboard, label: 'Dashboard',   path: '/dashboard' },
  { icon: BookOpen,        label: 'Learning',    path: '/learning' },
  { icon: Calculator,      label: 'Tools', path: '/calculators' },
  { icon: Users,           label: 'Community',   path: '/community' },
  { icon: UserIcon,        label: 'My Network',  path: '/network' },
];

// Secondary items — folded into the "More" dropdown.
const SECONDARY = [
  { icon: Bot,          label: 'AI Assistant', path: '/ai-chat',    hint: 'Ask a freeze-dry expert' },
  { icon: ChefHat,      label: 'Recipe Builder', path: '/recipes', hint: 'Pro · AI cycle + denaturing analysis', pro: true },
  { icon: Beaker,       label: 'Batch Analyzer', path: '/tools/batch-analyzer', hint: 'Pro · Compare cost & throughput', pro: true },
  { icon: Beaker,       label: 'Production Forecast', path: '/tools/production-forecast', hint: 'Pro · Monthly schedule + rollups', pro: true },
  { icon: Beaker,       label: 'Waste & Cost Optimizer', path: '/tools/waste-cost-optimizer', hint: 'Pro · AI substitutions', pro: true },
  { icon: Beaker,       label: 'Batches',      path: '/batch-tracker', hint: 'Log live cycles' },
  { icon: Video,        label: 'Videos',       path: '/videos',     hint: 'Video library' },
  { icon: Users,        label: 'Study rooms',  path: '/study',      hint: 'Peer study rooms — whiteboard & breakout' },
  { icon: StoreIcon,    label: 'Directory',    path: '/directory',  hint: 'Public operator directory' },
  { icon: ShoppingCart, label: 'Shop',         path: '/shop',       hint: 'Merch & tickets' },
  { icon: GraduationCap, label: 'Graduate wall', path: '/graduates', hint: 'Community certificate showcase' },
  { icon: Award,        label: 'Pricing',      path: '/pricing',    hint: 'Membership tiers' },
  { icon: UserIcon,     label: 'My portal',    path: '/portal',     hint: 'Your profile & certificates' },
];

const NavPill = ({ icon: Icon, label, path, active, onClick, testId }) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    data-testid={testId}
    className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-3.5 h-9 text-sm font-semibold transition-colors flex-shrink-0
      ${active
        ? 'bg-primary text-primary-foreground shadow-sm'
        : 'text-foreground/75 hover:text-foreground hover:bg-muted'
      }`}
  >
    <Icon className="w-4 h-4" />
    <span>{label}</span>
  </button>
);

const ToolSelector = ({ className = '', showBreadcrumbs = true }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, logout } = useContext(AuthContext);
  const { setOpen: setCmdkOpen } = useCommandPalette();
  const [moreOpen, setMoreOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);

  const isActive = (path) =>
    pathname === path || (path !== '/dashboard' && pathname.startsWith(path));

  const secondaryActive = useMemo(
    () => SECONDARY.some((t) => isActive(t.path)),
    [pathname],
  );

  const go = (path) => {
    setMoreOpen(false);
    setMobileOpen(false);
    setUserOpen(false);
    navigate(path);
  };

  return (
    <>
      <div
        className={`sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border ${className}`}
        data-testid="tool-selector"
      >
        <div className="container mx-auto px-4 sm:px-6">
          <div className="h-14 flex items-center gap-2" role="navigation" aria-label="Primary">
            {/* Brand */}
            <BrandMark
              size="sm"
              onClick={() => go('/dashboard')}
              testId="toolbar-brand"
              className="flex-shrink-0"
            />

            {/* Primary nav — desktop (≥1024px) */}
            <nav className="hidden lg:flex items-center gap-1 ml-3 min-w-0" aria-label="Primary tools">
              {PRIMARY.map((t) => (
                <NavPill
                  key={t.path}
                  {...t}
                  active={isActive(t.path)}
                  onClick={() => go(t.path)}
                  testId={`nav-${t.label.toLowerCase().replace(/\s+/g, '-')}`}
                />
              ))}

              {/* Topics hub dropdown — SEO discovery pillar */}
              <TopicsNavDropdown />

              {/* More dropdown */}
              <Popover open={moreOpen} onOpenChange={setMoreOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-haspopup="true"
                    aria-expanded={moreOpen}
                    data-testid="nav-more-trigger"
                    className={`inline-flex items-center gap-1.5 rounded-full px-3.5 h-9 text-sm font-semibold transition-colors flex-shrink-0
                      ${secondaryActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-foreground/75 hover:text-foreground hover:bg-muted'
                      }`}
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>More</span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  sideOffset={8}
                  className="w-[560px] p-3"
                  data-testid="nav-more-menu"
                >
                  <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-muted-foreground px-2 pt-1 pb-2">
                    All tools
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    {SECONDARY.map((t) => {
                      const active = isActive(t.path);
                      return (
                        <button
                          key={t.path}
                          type="button"
                          onClick={() => go(t.path)}
                          data-testid={`nav-more-${t.label.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`flex items-start gap-3 p-2.5 rounded-xl text-left transition-colors
                            ${active ? 'bg-primary/10' : 'hover:bg-muted'}`}
                        >
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0
                            ${active ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground/70'}`}>
                            <t.icon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <div className={`text-sm font-bold ${active ? 'text-primary' : 'text-foreground'}`}>
                              {t.label}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">{t.hint}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
            </nav>

            {/* Mobile / tablet hamburger — everything below lg */}
            <div className="lg:hidden ml-2">
              <Popover open={mobileOpen} onOpenChange={setMobileOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-label="Open navigation menu"
                    data-testid="nav-mobile-trigger"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-border bg-card text-foreground/85 hover:bg-muted hover:text-foreground font-semibold text-sm"
                  >
                    <Menu className="w-4 h-4" />
                    <span className="hidden sm:inline">Menu</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" sideOffset={8} className="w-[calc(100vw-32px)] max-w-sm p-2 max-h-[80vh] overflow-y-auto" data-testid="nav-mobile-menu">
                  <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-muted-foreground px-2 pt-1 pb-2">
                    Navigate
                  </div>
                  {[...PRIMARY, ...SECONDARY].map((t) => {
                    const active = isActive(t.path);
                    return (
                      <button
                        key={t.path}
                        type="button"
                        onClick={() => go(t.path)}
                        data-testid={`nav-mobile-${t.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-semibold transition-colors
                          ${active ? 'bg-primary text-primary-foreground' : 'text-foreground/85 hover:bg-muted'}`}
                      >
                        <t.icon className="w-4 h-4 flex-shrink-0" />
                        {t.label}
                      </button>
                    );
                  })}
                  {user && (
                    <>
                      <div className="h-px bg-border my-2" />
                      <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-muted-foreground px-2 pb-1">
                        Account
                      </div>
                      {user.role === 'admin' && (
                        <>
                          <button
                            type="button"
                            onClick={() => go('/admin/portal')}
                            className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-semibold text-foreground/85 hover:bg-muted"
                          >
                            <Settings className="w-4 h-4" /> Admin portal
                          </button>
                          <button
                            type="button"
                            onClick={() => go('/admin')}
                            className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-semibold text-foreground/85 hover:bg-muted"
                          >
                            <BarChart3 className="w-4 h-4" /> Analytics
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => { setMobileOpen(false); logout && logout(); navigate('/'); }}
                        className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-semibold text-destructive hover:bg-destructive/10"
                      >
                        <LogOut className="w-4 h-4" /> Sign out
                      </button>
                    </>
                  )}
                </PopoverContent>
              </Popover>
            </div>

            {/* Spacer */}
            <div className="flex-1 min-w-0" />

            {/* Right side — search (always visible), bell, user */}
            <button
              type="button"
              onClick={() => setCmdkOpen(true)}
              className="hidden md:inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 h-9 text-xs font-semibold text-muted-foreground hover:text-foreground hover:border-primary/60 transition-colors flex-shrink-0"
              data-testid="toolbar-cmdk-trigger"
              aria-label="Open command palette"
            >
              <Search className="w-3.5 h-3.5" />
              <span>Jump to…</span>
              <kbd className="hidden xl:inline-flex items-center gap-0.5 rounded border border-border/80 bg-background/60 px-1.5 py-0.5 text-[10px] font-bold text-foreground/70">
                <CommandIcon className="w-3 h-3" /> K
              </kbd>
            </button>
            <button
              type="button"
              onClick={() => setCmdkOpen(true)}
              className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-full border border-border text-foreground/80 hover:bg-muted flex-shrink-0"
              data-testid="toolbar-cmdk-trigger-mobile"
              aria-label="Open command palette"
            >
              <Search className="w-4 h-4" />
            </button>

            {user && (
              <button
                type="button"
                onClick={() => navigate('/live')}
                className="relative inline-flex items-center justify-center h-9 w-9 rounded-full border border-border hover:bg-muted transition-colors flex-shrink-0"
                aria-label="Live sessions"
                title="Live sessions"
                data-testid="live-sessions-btn"
              >
                <Video className="w-4 h-4 text-foreground" />
              </button>
            )}
            {user && (
              <button
                type="button"
                onClick={() => navigate('/help')}
                className="relative inline-flex items-center justify-center h-9 w-9 rounded-full border border-border hover:bg-muted transition-colors flex-shrink-0"
                aria-label="Help center"
                data-testid="help-center-btn"
              >
                <LifeBuoy className="w-4 h-4 text-foreground" />
              </button>
            )}
            {user && (
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('fda:open-report', { detail: { type: 'bug' } }))}
                className="relative inline-flex items-center justify-center h-9 w-9 rounded-full border border-border hover:bg-muted transition-colors flex-shrink-0"
                aria-label="Report a bug"
                title="Report a bug"
                data-testid="toolbar-report-bug"
              >
                <Bug className="w-4 h-4 text-foreground" />
              </button>
            )}
            {user && <DMInboxButton />}
            {user && <NotificationBell />}

            <ThemeToggle />
            <LanguageSwitcher />

            {user ? (
              <Popover open={userOpen} onOpenChange={setUserOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="toolbar-user-trigger"
                    className="inline-flex items-center gap-2 rounded-full border border-border pl-1 pr-2 h-9 hover:bg-muted transition-colors flex-shrink-0"
                    aria-label="Open user menu"
                  >
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-[11px] font-bold">
                      {(user.email || 'U').slice(0, 1).toUpperCase()}
                    </span>
                    <ChevronDown className={`w-3.5 h-3.5 text-foreground/60 transition-transform ${userOpen ? 'rotate-180' : ''}`} />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" sideOffset={8} className="w-64 p-2" data-testid="toolbar-user-menu">
                  <div className="px-2 pt-1 pb-2">
                    <div className="text-sm font-bold text-foreground truncate">{user.email}</div>
                    <div className="text-[10px] uppercase tracking-widest text-primary font-bold mt-0.5">
                      {(user.role || 'free')} member
                    </div>
                  </div>
                  <div className="h-px bg-border my-1" />
                  <button
                    type="button"
                    onClick={() => go('/portal')}
                    className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm font-semibold text-foreground/80 hover:bg-muted"
                    data-testid="toolbar-user-portal"
                  >
                    <UserIcon className="w-4 h-4" /> My portal
                  </button>
                  <button
                    type="button"
                    onClick={() => go('/pricing')}
                    className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm font-semibold text-foreground/80 hover:bg-muted"
                    data-testid="toolbar-user-pricing"
                  >
                    <Award className="w-4 h-4" /> Pricing
                  </button>
                  {user.role === 'admin' && (
                    <>
                      <div className="h-px bg-border my-1" />
                      <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-muted-foreground px-2 pt-1 pb-1">
                        Admin
                      </div>
                      <button
                        type="button"
                        onClick={() => go('/admin/portal')}
                        className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm font-semibold text-foreground/80 hover:bg-muted"
                        data-testid="toolbar-user-admin-portal"
                      >
                        <Settings className="w-4 h-4" /> Admin portal
                      </button>
                      <button
                        type="button"
                        onClick={() => go('/admin')}
                        className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm font-semibold text-foreground/80 hover:bg-muted"
                        data-testid="toolbar-user-analytics"
                      >
                        <BarChart3 className="w-4 h-4" /> Analytics
                      </button>
                    </>
                  )}
                  <div className="h-px bg-border my-1" />
                  <button
                    type="button"
                    onClick={() => { setUserOpen(false); logout && logout(); navigate('/'); }}
                    className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm font-semibold text-destructive hover:bg-destructive/10"
                    data-testid="toolbar-user-signout"
                  >
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </PopoverContent>
              </Popover>
            ) : (
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 h-9 text-sm font-bold hover:bg-primary/90 transition-colors"
                data-testid="toolbar-login"
              >
                Log in
              </button>
            )}
          </div>
        </div>
      </div>
      {showBreadcrumbs && <AppBreadcrumbs />}
    </>
  );
};

export default ToolSelector;
